import { useEffect, useMemo, useRef, useState } from "react"
import { ActionIcon, Badge, Box, Center, Flex, Group, Loader, Paper, ScrollArea, SegmentedControl, Select, Stack, Text, ThemeIcon, Tooltip } from "@mantine/core"
import { IconMapPin, IconRefresh, IconTruckDelivery } from "@tabler/icons-react"
import { GoogleMap, InfoWindow, Marker, useJsApiLoader } from "@react-google-maps/api"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import "dayjs/locale/es"
import { headerStore } from "@shared/state/header.state.ts"
import { useCountryScope } from "@/shared/country/use-country-scope"
import { ALL_COUNTRIES, COUNTRY_SELECTION_OPTIONS, countryQueryParam, mapCenterOf, workingCountry } from "@/shared/country/country-scope"
import { LiveLocation, useLiveLocations } from "../hooks/use-live-locations"

dayjs.extend(relativeTime)
dayjs.locale("es")

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string
const BIOCELLS_GREEN = "#9EB516"

const RIDER_TYPE_LABEL: Record<string, string> = {
  staff: "Propio",
  third_party: "Tercero",
  warehouse: "Almacén"
}

const RIDER_TYPE_COLOR: Record<string, string> = {
  staff: "blue",
  third_party: "orange",
  warehouse: "grape"
}

const STOP_ACTION_LABEL: Record<string, string> = {
  delivery: "Entrega",
  pickup: "Recojo"
}

const STOP_ACTION_COLOR: Record<string, string> = {
  delivery: "teal",
  pickup: "grape"
}

const STOP_ACTION_HEX: Record<string, string> = {
  delivery: "#0ca678",
  pickup: "#ae3ec9"
}

/** Ni activo ni sin señal -- todavía no manda su primera ubicación, se muestra en su punto de partida aproximado */
const PENDING_FILL = "#adb5bd"

/** Van (no moto): así se movilizan los repartidores de Biocells */
function vanMarkerSvg(fillFreshness: string, action?: string): string {
  const ringColor = action ? STOP_ACTION_HEX[action] ?? "white" : "white"
  return `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
    <circle cx="18" cy="18" r="16" fill="${fillFreshness}" stroke="${ringColor}" stroke-width="3" stroke-dasharray="${fillFreshness === PENDING_FILL ? "3,2" : "0"}"/>
    <g transform="translate(7,11)">
      <path d="M1 12.5 L1 5.5 Q1 3.5 3 3.5 L13.5 3.5 Q15.3 3.5 16.4 5 L19.3 9 Q20 10 20 11.3 L20 12.5 Z" fill="${BIOCELLS_GREEN}" stroke="white" stroke-width="0.8"/>
      <rect x="14.2" y="5" width="4.2" height="4" rx="0.6" fill="white" opacity="0.9"/>
      <circle cx="5" cy="13.2" r="2.4" fill="#2b2b2b" stroke="white" stroke-width="0.7"/>
      <circle cx="16.5" cy="13.2" r="2.4" fill="#2b2b2b" stroke="white" stroke-width="0.7"/>
    </g>
  </svg>`
}

const RETORNO_OFICINA = "retorno_a_oficina"
const OFICINA_HEX = "#495057"

function oficinaPinSvg(): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40">
    <path d="M15 1C7.3 1 1 7.1 1 14.7 1 25 15 39 15 39s14-14 14-24.3C29 7.1 22.7 1 15 1z" fill="${OFICINA_HEX}" stroke="white" stroke-width="2"/>
    <text x="15" y="19" text-anchor="middle" font-family="Arial, sans-serif" font-size="11" font-weight="700" fill="white">OF</text>
  </svg>`
}

/** "Llega 11:32 · en 25 min · 8.2 km" -- se recalcula con cada consulta del mapa (cada 5 s) */
function etaTexto(loc: LiveLocation): string | null {
  if (!loc.etaAt) return null
  const minutos = Math.max(0, Math.round((new Date(loc.etaAt).getTime() - Date.now()) / 60000))
  const cuando = minutos === 0 ? "llegando" : `en ${minutos} min`
  const km = loc.etaDistanceMeters != null ? ` · ${(loc.etaDistanceMeters / 1000).toFixed(1)} km` : ""
  return `Llega ${dayjs(loc.etaAt).format("HH:mm")} · ${cuando}${km}`
}

function secondsAgo(updatedAt: string): number {
  return Math.floor((Date.now() - new Date(updatedAt).getTime()) / 1000)
}

function formatElapsed(updatedAt: string): string {
  const secs = secondsAgo(updatedAt)
  if (secs < 60) return `hace ${secs}s`
  if (secs < 3600) return `hace ${Math.floor(secs / 60)}min`
  return dayjs(updatedAt).fromNow()
}

/** Filtro "Estado" del panel: qué está haciendo el repartidor, o cómo está su señal */
const FILTRO_ESTADO_OPCIONES = [
  { value: "entrega", label: "En entrega" },
  { value: "recojo", label: "En recojo" },
  { value: "retorno", label: "Retorno a oficina" },
  { value: "sin_senal", label: "Sin señal (más de 5 min)" },
  { value: "esperando", label: "Esperando ubicación" }
]

function coincideEstado(loc: LiveLocation, estado: string | null): boolean {
  switch (estado) {
    case "entrega":
      return loc.action === "delivery"
    case "recojo":
      return loc.action === "pickup" && loc.odooState !== RETORNO_OFICINA
    case "retorno":
      return loc.odooState === RETORNO_OFICINA
    case "sin_senal":
      return !loc.atOrigin && secondsAgo(loc.updatedAt) > 300
    case "esperando":
      return !!loc.atOrigin
    default:
      return true
  }
}

export default function LiveRidersMapPage() {
  const { isLoaded: mapsLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_API_KEY })
  // País (EC-15): el admin elige Todos (como siempre), Perú o Ecuador; al resto el back le da los de su país.
  // El mapa se centra en Lima o Quito según el país (Todos = Lima, como siempre).
  const countryScope = useCountryScope()
  const [countrySelection, setCountrySelection] = useState<string>(ALL_COUNTRIES)
  const countryParam = countryQueryParam(countryScope, countrySelection)
  const mapCenter = mapCenterOf(workingCountry(countryScope, countrySelection))
  const { locations, isLoading, error, lastUpdated, refetch } = useLiveLocations(countryParam)
  const [selected, setSelected] = useState<LiveLocation | null>(null)
  const mapRef = useRef<google.maps.Map | null>(null)
  const [riderFilter, setRiderFilter] = useState<string | null>(null)
  const [estadoFilter, setEstadoFilter] = useState<string | null>(null)

  const riderOptions = useMemo(
    () => locations.map((l) => ({ value: l.riderId, label: l.riderName })).sort((a, b) => a.label.localeCompare(b.label, "es")),
    [locations]
  )
  const hayFiltro = riderFilter !== null || estadoFilter !== null
  const visibleLocations = locations.filter((l) => (riderFilter === null || l.riderId === riderFilter) && coincideEstado(l, estadoFilter))

  const oficinas = [
    ...new Map(
      visibleLocations
        .filter((l) => l.odooState === RETORNO_OFICINA && l.destinoLat != null && l.destinoLng != null)
        .map((l) => [`${l.destinoLat},${l.destinoLng}`, { lat: l.destinoLat as number, lng: l.destinoLng as number }])
    ).values()
  ]

  useEffect(() => {
    headerStore.setTitle("Mapa en Vivo — Motorizados")
    headerStore.setOnRefresh(() => refetch())
    return () => {
      headerStore.setTitle(null)
      headerStore.setOnRefresh(null)
    }
  }, [refetch])

  const panToRider = (loc: LiveLocation) => {
    setSelected(loc)
    mapRef.current?.panTo({ lat: loc.latitud, lng: loc.longitud })
    mapRef.current?.setZoom(15)
  }

  const onRiderFilterChange = (riderId: string | null) => {
    setRiderFilter(riderId)
    const loc = riderId ? locations.find((l) => l.riderId === riderId) : undefined
    if (loc) panToRider(loc)
  }

  const onCountryChange = (next: string) => {
    setCountrySelection(next)
    setRiderFilter(null)
    setSelected(null)
    mapRef.current?.setZoom(12)
  }

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <Center h={400}>
        <Text c="dimmed">Configura VITE_GOOGLE_MAPS_API_KEY para ver el mapa</Text>
      </Center>
    )
  }

  return (
    <Stack
      gap="md"
      style={{
        height: "calc(100dvh - var(--app-shell-header-offset, 60px) - 2rem)",
        minHeight: 0
      }}
    >
      {/* Encabezado de estado */}
      <Group justify="space-between">
        <Group gap="xs">
          <ThemeIcon variant="light" color="green" size="sm">
            <IconTruckDelivery size={14} />
          </ThemeIcon>
          <Text size="sm" fw={500}>
            {isLoading && locations.length === 0
              ? "Cargando…"
              : `${hayFiltro ? `${visibleLocations.length} de ${locations.length}` : locations.length} motorizado${(hayFiltro ? visibleLocations.length : locations.length) !== 1 ? "s" : ""} en el mapa`}
          </Text>
          {lastUpdated && (
            <Text size="xs" c="dimmed">
              · actualizado {dayjs(lastUpdated).format("HH:mm:ss")}
            </Text>
          )}
        </Group>
        <Group gap="xs">
          {countryScope.canSeeAllCountries && <SegmentedControl size="xs" aria-label="País" value={countrySelection} onChange={onCountryChange} data={COUNTRY_SELECTION_OPTIONS} />}
          <Tooltip label="Actualizar ahora">
            <ActionIcon variant="subtle" onClick={refetch} loading={isLoading}>
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {error && (
        <Text c="red" size="sm">
          {error}
        </Text>
      )}

      <Flex direction={{ base: "column", md: "row" }} align="stretch" gap="md" style={{ flex: 1, minHeight: 0 }}>
        {/* Panel lateral */}
        <Paper
          withBorder
          p="xs"
          style={{ display: "flex", flexDirection: "column", minHeight: 0 }}
          w={{ base: "100%", md: 240 }}
          h={{ base: 300, md: "auto" }}
        >
          <Text size="xs" fw={600} c="dimmed" mb="xs" tt="uppercase">
            Motorizados en ruta
          </Text>
          {locations.length > 0 && (
            <Stack gap={6} mb="xs">
              <Select
                size="xs"
                placeholder="Todos los motorizados"
                aria-label="Filtrar por motorizado"
                data={riderOptions}
                value={riderFilter}
                onChange={onRiderFilterChange}
                searchable
                clearable
                nothingFoundMessage="Sin coincidencias"
              />
              <Select
                size="xs"
                placeholder="Todos los estados"
                aria-label="Filtrar por estado"
                data={FILTRO_ESTADO_OPCIONES}
                value={estadoFilter}
                onChange={setEstadoFilter}
                clearable
              />
            </Stack>
          )}
          {isLoading && locations.length === 0 ? (
            <Center py="md">
              <Loader size="sm" />
            </Center>
          ) : locations.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="md">
              Sin motorizados activos en las últimas 6 h
            </Text>
          ) : visibleLocations.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="md">
              Ningún motorizado coincide con el filtro
            </Text>
          ) : (
            <ScrollArea style={{ flex: 1 }}>
              <Stack gap={6}>
                {visibleLocations.map((loc) => {
                  const isSelected = selected?.riderId === loc.riderId
                  const secs = secondsAgo(loc.updatedAt)
                  const staleness = loc.atOrigin ? "gray" : secs > 300 ? "red" : secs > 60 ? "yellow" : "green"
                  const eta = etaTexto(loc)
                  return (
                    <Paper
                      key={loc.riderId}
                      withBorder
                      p="xs"
                      style={{ cursor: "pointer", borderColor: isSelected ? "var(--mantine-color-blue-5)" : undefined }}
                      onClick={() => panToRider(loc)}
                    >
                      <Group gap="xs" wrap="nowrap">
                        <ThemeIcon size="sm" variant="light" color={staleness}>
                          <IconTruckDelivery size={12} />
                        </ThemeIcon>
                        <Box style={{ minWidth: 0 }}>
                          <Text size="sm" fw={500} truncate>
                            {loc.riderName}
                          </Text>
                          <Group gap={4}>
                            <Badge size="xs" variant="light" color={RIDER_TYPE_COLOR[loc.riderType] ?? "gray"}>
                              {RIDER_TYPE_LABEL[loc.riderType] ?? loc.riderType}
                            </Badge>
                            {loc.action && (
                              <Badge size="xs" variant="light" color={STOP_ACTION_COLOR[loc.action] ?? "gray"}>
                                {STOP_ACTION_LABEL[loc.action] ?? loc.action}
                              </Badge>
                            )}
                            {loc.odooState === RETORNO_OFICINA && (
                              <Badge size="xs" variant="filled" color="grape">
                                Retorno a oficina
                              </Badge>
                            )}
                          </Group>
                          {eta && (
                            <Text size="xs" fw={600} c="grape.7">
                              {eta}
                            </Text>
                          )}
                          <Text size="xs" c="dimmed">
                            {loc.atOrigin ? "Esperando ubicación — en punto de partida" : formatElapsed(loc.updatedAt)}
                          </Text>
                          {loc.saleOrderId && (
                            <Text size="xs" c="dimmed" truncate>
                              SO: {loc.saleOrderName ?? loc.saleOrderId}
                            </Text>
                          )}
                        </Box>
                      </Group>
                    </Paper>
                  )
                })}
              </Stack>
            </ScrollArea>
          )}
        </Paper>

        {/* Mapa */}
        <Box style={{ flex: 1, minHeight: 320, borderRadius: 8, overflow: "hidden" }}>
          {!mapsLoaded ? (
            <Center h="100%"><Loader /></Center>
          ) : (
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={mapCenter}
              zoom={12}
              onLoad={(map) => { mapRef.current = map }}
            >
              {visibleLocations.map((loc) => (
                <Marker
                  key={loc.riderId}
                  position={{ lat: loc.latitud, lng: loc.longitud }}
                  title={loc.riderName}
                  icon={{
                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                      vanMarkerSvg(loc.atOrigin ? PENDING_FILL : secondsAgo(loc.updatedAt) > 300 ? "#fa5252" : "#228be6", loc.action)
                    )}`,
                    scaledSize: new window.google.maps.Size(36, 36),
                    anchor: new window.google.maps.Point(18, 18)
                  }}
                  onClick={() => setSelected(loc)}
                />
              ))}

              {oficinas.map((oficina) => (
                <Marker
                  key={`oficina-${oficina.lat},${oficina.lng}`}
                  position={oficina}
                  title="Oficina (destino del retorno)"
                  zIndex={1}
                  icon={{
                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(oficinaPinSvg())}`,
                    scaledSize: new window.google.maps.Size(30, 40),
                    anchor: new window.google.maps.Point(15, 39)
                  }}
                />
              ))}

              {selected && visibleLocations.some((l) => l.riderId === selected.riderId) && (
                <InfoWindow
                  position={{ lat: selected.latitud, lng: selected.longitud }}
                  onCloseClick={() => setSelected(null)}
                >
                  <div style={{ minWidth: 160 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>🚐 {selected.riderName}</div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      {RIDER_TYPE_LABEL[selected.riderType] ?? selected.riderType}
                      {selected.action && (
                        <span style={{ color: STOP_ACTION_HEX[selected.action] ?? "#666", fontWeight: 600 }}>
                          {" · "}{STOP_ACTION_LABEL[selected.action] ?? selected.action}
                        </span>
                      )}
                    </div>
                    {selected.saleOrderId && (
                      <div style={{ fontSize: 12, marginTop: 4 }}>
                        <span style={{ color: "#888" }}>SO: </span>{selected.saleOrderName ?? selected.saleOrderId}
                      </div>
                    )}
                    {selected.odooState === RETORNO_OFICINA && (
                      <div style={{ fontSize: 12, marginTop: 4, color: "#862e9c", fontWeight: 600 }}>
                        Retorno a oficina{selected.stopCorrelativo ? ` · ${selected.stopCorrelativo}` : ""}
                        {etaTexto(selected) && <div style={{ fontWeight: 500 }}>{etaTexto(selected)}</div>}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: "#888", marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
                      <span>📍</span>
                      <span>{selected.latitud.toFixed(5)}, {selected.longitud.toFixed(5)}</span>
                    </div>
                    <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                      {selected.atOrigin ? "⏱ Esperando primera ubicación — punto de partida aproximado" : `⏱ ${formatElapsed(selected.updatedAt)}`}
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <a
                        href={`https://www.google.com/maps?q=${selected.latitud},${selected.longitud}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: 11, color: "#228be6" }}
                      >
                        Ver en Google Maps
                      </a>
                      {" · "}
                      <a
                        href={`https://waze.com/ul?ll=${selected.latitud},${selected.longitud}&navigate=yes`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: 11, color: "#33ccff" }}
                      >
                        Waze
                      </a>
                    </div>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          )}
        </Box>
      </Flex>

      {/* Leyenda */}
      <Group gap="lg" wrap="wrap">
        <Group gap={6}>
          <Box w={12} h={12} style={{ borderRadius: "50%", background: "#228be6" }} />
          <Text size="xs" c="dimmed">Activo (menos de 5 min)</Text>
        </Group>
        <Group gap={6}>
          <Box w={12} h={12} style={{ borderRadius: "50%", background: "#fa5252" }} />
          <Text size="xs" c="dimmed">Sin señal (más de 5 min)</Text>
        </Group>
        <Group gap={6}>
          <Box w={12} h={12} style={{ borderRadius: "50%", background: PENDING_FILL, border: "1px dashed white" }} />
          <Text size="xs" c="dimmed">Esperando ubicación (punto de partida aproximado)</Text>
        </Group>
        <Group gap={6}>
          <Box w={12} h={12} style={{ borderRadius: "50%", border: `3px solid ${STOP_ACTION_HEX.delivery}` }} />
          <Text size="xs" c="dimmed">Punto de entrega</Text>
        </Group>
        <Group gap={6}>
          <Box w={12} h={12} style={{ borderRadius: "50%", border: `3px solid ${STOP_ACTION_HEX.pickup}` }} />
          <Text size="xs" c="dimmed">Punto de recojo</Text>
        </Group>
        <Group gap={6}>
          <Box w={12} h={12} style={{ borderRadius: "50% 50% 50% 0", transform: "rotate(-45deg)", background: OFICINA_HEX }} />
          <Text size="xs" c="dimmed">Oficina (destino del retorno)</Text>
        </Group>
        <Group gap="xs">
          <IconMapPin size={12} color="gray" />
          <Text size="xs" c="dimmed">Se actualiza cada 5 segundos</Text>
        </Group>
      </Group>
    </Stack>
  )
}
